import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useNavigate } from "react-router-dom";
import Tilt from "react-parallax-tilt";

import Particle from "../Particle";
import Testimonials from "./Testimonials";
import reviews from "./data";
import useInViewOnce from "../../hooks/useInViewOnce";

import seniorsTraining from "../../Assets/seniors_training.jpg";
import seniors1 from "../../Assets/senior1.jpg";
import seniors2 from "../../Assets/seniors2.jpg";
import seniors4 from "../../Assets/seniors4.jpg";
import seniors3 from "../../Assets/seniors3.png";
import seniors5 from "../../Assets/seniors5.jpg";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import SlickLazyImage from "../shared/SlickLazyImage";
import { useTranslation } from "react-i18next";

const sliderImages = [seniors1, seniors2, seniors4, seniors3, seniors5];

function Senior() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeSlide, setActiveSlide] = React.useState(0);

  const handleRegister = () => {
    navigate("/registration#seniors-section");
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2400,
    pauseOnHover: true,
    pauseOnFocus: true,
    adaptiveHeight: false,
    lazyLoad: "ondemand",
    beforeChange: (_current, next) => setActiveSlide(next),
  };

  const videoId = "5mPU-mIQgoQ";

  const sliderAltsRaw = t("programs.seniors.sliderAlts", { returnObjects: true });
  const sliderAlts = Array.isArray(sliderAltsRaw) ? sliderAltsRaw : [];

  const skillsTaughtRaw = t("programs.seniors.skills.items", { returnObjects: true });
  const skillsTaught = Array.isArray(skillsTaughtRaw) ? skillsTaughtRaw : [];

  const whyItMattersRaw = t("programs.seniors.why.items", { returnObjects: true });
  const whyItMatters = Array.isArray(whyItMattersRaw) ? whyItMattersRaw : [];

  const approachRaw = t("programs.seniors.approach.items", { returnObjects: true });
  const approach = Array.isArray(approachRaw) ? approachRaw : [];

  const { ref: topRef, hasEntered: topSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: overviewRef, hasEntered: overviewSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: whyRef, hasEntered: whySeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: approachRef, hasEntered: approachSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: videoRef, hasEntered: videoSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: testimonialsRef, hasEntered: testimonialsSeen } = useInViewOnce({ threshold: 0.18, once: true });

  return (
    <Container fluid className="about-section seniors-page">
      <Particle />

      <Container className="seniors-container">
        <header className="seniors-hero">
          <h1 className="seniors-hero-title">
            {t("programs.seniors.hero.titlePrefix")} <strong className="purple">{t("programs.seniors.hero.titleEmphasis")}</strong> {t("programs.seniors.hero.titleSuffix")}
          </h1>
          <p className="seniors-hero-lead">
            {t("programs.seniors.hero.lead")}
          </p>
          <div className="seniors-hero-cta">
            <Button onClick={handleRegister} className="custom-yellow-btn seniors-cta-btn">
              {t("common.registerNow")}
            </Button>
          </div>
        </header>

        <div ref={topRef} className={`seniors-reveal${topSeen ? " is-seen" : ""}`}>
          <Row className="seniors-top" style={{ justifyContent: "center" }}>
            <Col md={7} className="seniors-left">
              <div className="policy-card ambassadors-slider-card" aria-label={t("programs.seniors.aria.highlights")}>
                <Slider {...settings}>
                  {sliderImages.map((imageSrc, index) => (
                    <div key={index} className="ambassadors-slide">
                      <SlickLazyImage
                        src={imageSrc}
                        alt={sliderAlts[index] || t("programs.seniors.sliderAltFallback")}
                        className="ambassadors-slide-img"
                        index={index}
                        activeIndex={activeSlide}
                        total={sliderImages.length}
                        buffer={2}
                        eager
                      />
                    </div>
                  ))}
                </Slider>
              </div>
            </Col>

            <Col md={5} className="seniors-right">
              <div className="policy-card seniors-summary-card">
                <h2 className="seniors-card-title">{t("programs.seniors.snapshot.title")}</h2>
                <p className="about-text seniors-card-text">
                  {t("programs.seniors.snapshot.text")}
                </p>
                <ul className="seniors-bullets">
                  {t("programs.seniors.snapshot.bullets", { returnObjects: true }).map((item, index) => (
                    <li key={`${index}-${item}`}>{item}</li>
                  ))}
                </ul>
                <Button onClick={handleRegister} className="custom-yellow-btn seniors-cta-btn seniors-cta-btn-secondary">
                  {t("common.registerHere")}
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        <section
          ref={overviewRef}
          className={`seniors-section seniors-reveal${overviewSeen ? " is-seen" : ""}`}
          aria-label={t("programs.seniors.aria.skills")}
        >
          <Row className="seniors-overview-row" style={{ justifyContent: "center" }}>
            <Col md={7} className="seniors-overview-left">
              <div className="policy-card seniors-copy-card">
                <h2 className="seniors-section-heading">{t("programs.seniors.skills.title")}</h2>
                <p className="seniors-body">
                  {t("programs.seniors.skills.lead")}
                </p>
                <ul className="seniors-list">
                  {skillsTaught.map((item, index) => (
                    <li key={`${index}-${item}`}>{item}</li>
                  ))}
                </ul>
              </div>
            </Col>

            <Col md={5} className="seniors-overview-right">
              <div className="policy-card seniors-image-card seniors-image-card--side" aria-label={t("programs.seniors.aria.portrait")}>
                <Tilt glareEnable={false}>
                  <img
                    src={seniorsTraining}
                    className="img-fluid seniors-image"
                    alt={t("programs.seniors.images.trainingAlt")}
                    loading="lazy"
                    decoding="async"
                  />
                </Tilt>
              </div>
            </Col>
          </Row>
        </section>

        <section
          ref={whyRef}
          className={`seniors-section seniors-reveal${whySeen ? " is-seen" : ""}`}
          aria-label={t("programs.seniors.aria.why")}
        >
          <Row>
            <Col md={12}>
              <div className="policy-card seniors-copy-card">
                <h2 className="seniors-section-heading">{t("programs.seniors.why.title")}</h2>
                <p className="seniors-body">{t("programs.seniors.why.lead")}</p>
                <ul className="seniors-list">
                  {whyItMatters.map((item, index) => (
                    <li key={`${index}-${item}`}>{item}</li>
                  ))}
                </ul>
                <h3 className="seniors-subheading">{t("programs.seniors.mission.title")}</h3>
                <p className="seniors-body">
                  {t("programs.seniors.mission.p1")}
                </p>
              </div>
            </Col>
          </Row>
        </section>

        <section
          ref={approachRef}
          className={`seniors-section seniors-reveal${approachSeen ? " is-seen" : ""}`}
          aria-label={t("programs.seniors.aria.approach")}
        >
          <h2 className="seniors-section-heading seniors-section-heading-center">{t("programs.seniors.approach.title")}</h2>
          <div className="seniors-grid">
            {approach.map((item) => (
              <div key={item.title} className="policy-card seniors-feature-card">
                <h3 className="seniors-feature-title">{item.title}</h3>
                <p className="seniors-feature-text">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          ref={videoRef}
          className={`seniors-section seniors-reveal${videoSeen ? " is-seen" : ""}`}
          aria-label={t("programs.seniors.aria.video")}
        >
          <h2 className="seniors-section-heading seniors-section-heading-center">{t("programs.seniors.videoTitle")}</h2>
          <div className="policy-card seniors-video-card">
            <div className="seniors-video">
              <iframe
                title={t("programs.seniors.videoIframeTitle")}
                src={`https://www.youtube.com/embed/${videoId}`}
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        <section
          ref={testimonialsRef}
          className={`seniors-testimonials-section seniors-reveal${testimonialsSeen ? " is-seen" : ""}`}
          aria-label={t("common.testimonials")}
        >
          <h2 className="seniors-section-heading seniors-section-heading-center">{t("common.testimonials")}</h2>
          <div className="impact-testimonials">
            <Testimonials reviews={reviews} />
          </div>
        </section>
      </Container>
    </Container>
  );
}

export default Senior;
